/**
 * OrderParticipants Component
 * Displays all parties involved in an order with clear internal/external distinction
 */

import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Divider,
  Chip,
  Button,
  Stack,
  Grid,
  CircularProgress,
  Alert,
  Avatar,
} from "@mui/material";
import {
  Phone,
  Email,
  WhatsApp,
  Person,
  LocalShipping,
  Business,
  Inventory,
  ContactPhone,
} from "@mui/icons-material";
import { getOrderParticipants } from "../services/orderService.js";
import { logError } from "@/shared/utils/logger.js";

const formatPhone = (phone) => {
  if (!phone) return null;
  // Format South African phone numbers
  const cleaned = phone.replace(/\D/g, "");
  if (cleaned.startsWith("27")) {
    return `+${cleaned.slice(0, 2)} ${cleaned.slice(2, 4)} ${cleaned.slice(4, 7)} ${cleaned.slice(7)}`;
  }
  return phone;
};

const getWhatsAppLink = (phone) => {
  if (!phone) return null;
  const cleaned = phone.replace(/\D/g, "");
  const whatsappNumber = cleaned.startsWith("27") ? cleaned : `27${cleaned.slice(1)}`;
  return `https://wa.me/${whatsappNumber}`;
};

const ParticipantCard = ({ participant, type }) => {
  const isInternal = type === "internal";
  const hasPhone = Boolean(participant.phone);
  const hasEmail = Boolean(participant.email);
  const whatsappLink = getWhatsAppLink(participant.phone);

  return (
    <Card
      variant="outlined"
      sx={{
        mb: 1.5,
        borderLeft: `4px solid ${isInternal ? "#1976d2" : "#ed6c02"}`,
        backgroundColor: isInternal ? "rgba(25, 118, 210, 0.04)" : "rgba(237, 108, 2, 0.04)",
      }}
    >
      <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
        <Box display="flex" alignItems="flex-start" justifyContent="space-between" gap={1} mb={1}>
          <Box display="flex" alignItems="center" gap={1} flex={1}>
            <Avatar
              sx={{
                width: 32,
                height: 32,
                bgcolor: isInternal ? "primary.main" : "warning.main",
                fontSize: "0.875rem",
              }}
            >
              {participant.name?.charAt(0)?.toUpperCase() || "?"}
            </Avatar>
            <Box flex={1} minWidth={0}>
              <Typography variant="subtitle2" fontWeight={700} noWrap>
                {participant.name || "Unknown"}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {participant.role || "Participant"}
              </Typography>
            </Box>
          </Box>
          <Chip
            label={isInternal ? "Internal" : "External"}
            size="small"
            color={isInternal ? "primary" : "warning"}
            variant="outlined"
          />
        </Box>

        {participant.category && (
          <Chip
            label={participant.category}
            size="small"
            variant="outlined"
            sx={{ mb: 1, fontSize: "0.7rem" }}
          />
        )}

        {(participant.phone || participant.email || participant.contactPerson) && (
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
            {hasPhone && (
              <>
                <Button
                  size="small"
                  startIcon={<Phone fontSize="small" />}
                  href={`tel:${participant.phone}`}
                  sx={{ textTransform: "none", fontSize: "0.75rem", minWidth: "auto", px: 1 }}
                >
                  Call
                </Button>
                {whatsappLink && (
                  <Button
                    size="small"
                    startIcon={<WhatsApp fontSize="small" />}
                    href={whatsappLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ textTransform: "none", fontSize: "0.75rem", minWidth: "auto", px: 1 }}
                  >
                    WhatsApp
                  </Button>
                )}
              </>
            )}
            {hasEmail && (
              <Button
                size="small"
                startIcon={<Email fontSize="small" />}
                href={`mailto:${participant.email}`}
                sx={{ textTransform: "none", fontSize: "0.75rem", minWidth: "auto", px: 1 }}
              >
                Email
              </Button>
            )}
            {participant.contactPerson && (
              <Typography variant="caption" color="text.secondary" sx={{ alignSelf: "center" }}>
                Contact: {participant.contactPerson}
              </Typography>
            )}
          </Stack>
        )}

        {participant.phone && (
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
            {formatPhone(participant.phone)}
          </Typography>
        )}

        {participant.partnerId && (
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
            Partner ID: {participant.partnerId}
          </Typography>
        )}

        {participant.licenseNumber && (
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
            License: {participant.licenseNumber}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

export default function OrderParticipants({ orderId }) {
  const [participants, setParticipants] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!orderId) {
      setLoading(false);
      return;
    }

    const fetchParticipants = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await getOrderParticipants(orderId);
        if (response.success) {
          setParticipants(response.data);
        } else {
          setError(response.error || "Failed to load participants");
        }
      } catch (err) {
        logError("Error fetching order participants", {
          orderId,
          error: err.message,
        });
        setError(err.message || "Failed to load participants");
      } finally {
        setLoading(false);
      }
    };

    fetchParticipants();
  }, [orderId]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={3}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!participants) {
    return (
      <Alert severity="info" sx={{ mb: 2 }}>
        No participants data available
      </Alert>
    );
  }

  const { internal = [], external = {} } = participants;
  const hasExternal = Boolean(
    external.customer ||
    (external.drivers && external.drivers.length > 0) ||
    (external.partners && external.partners.length > 0) ||
    (external.suppliers && external.suppliers.length > 0) ||
    external.contacts?.pickup ||
    external.contacts?.delivery ||
    external.contacts?.dayOf
  );

  return (
    <Box>
      <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
        Order Participants
      </Typography>

      <Grid container spacing={3}>
        {/* External Participants */}
        {hasExternal && (
          <Grid item xs={12} md={6}>
            <Card variant="outlined" sx={{ mb: 2 }}>
              <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: "50%",
                    bgcolor: "warning.main",
                  }}
                />
                <Typography variant="subtitle1" fontWeight={700}>
                  External Parties
                </Typography>
              </Box>
                <Divider sx={{ mb: 2 }} />

                {/* Customer */}
                {external.customer && (
                  <Box mb={2}>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <Person fontSize="small" color="primary" />
                      <Typography variant="subtitle2" fontWeight={600}>
                        Customer
                      </Typography>
                    </Box>
                    <ParticipantCard participant={external.customer} type="external" />
                  </Box>
                )}

                {/* Drivers */}
                {external.drivers && external.drivers.length > 0 && (
                  <Box mb={2}>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <LocalShipping fontSize="small" color="primary" />
                      <Typography variant="subtitle2" fontWeight={600}>
                        Drivers ({external.drivers.length})
                      </Typography>
                    </Box>
                    {external.drivers.map((driver, idx) => (
                      <ParticipantCard key={driver.id || idx} participant={driver} type="external" />
                    ))}
                  </Box>
                )}

                {/* Partners */}
                {external.partners && external.partners.length > 0 && (
                  <Box mb={2}>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <Business fontSize="small" color="primary" />
                      <Typography variant="subtitle2" fontWeight={600}>
                        Partners ({external.partners.length})
                      </Typography>
                    </Box>
                    {external.partners.map((partner, idx) => (
                      <ParticipantCard key={partner.id || idx} participant={partner} type="external" />
                    ))}
                  </Box>
                )}

                {/* Suppliers */}
                {external.suppliers && external.suppliers.length > 0 && (
                  <Box mb={2}>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <Inventory fontSize="small" color="primary" />
                      <Typography variant="subtitle2" fontWeight={600}>
                        Product Suppliers ({external.suppliers.length})
                      </Typography>
                    </Box>
                    {external.suppliers.map((supplier, idx) => (
                      <ParticipantCard key={supplier.id || idx} participant={supplier} type="external" />
                    ))}
                  </Box>
                )}

                {/* Site Contacts */}
                {(external.contacts?.pickup ||
                  external.contacts?.delivery ||
                  external.contacts?.dayOf) && (
                  <Box mb={2}>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <ContactPhone fontSize="small" color="primary" />
                      <Typography variant="subtitle2" fontWeight={600}>
                        Site Contacts
                      </Typography>
                    </Box>
                    {external.contacts.pickup && (
                      <ParticipantCard participant={external.contacts.pickup} type="external" />
                    )}
                    {external.contacts.delivery && (
                      <ParticipantCard participant={external.contacts.delivery} type="external" />
                    )}
                    {external.contacts.dayOf && (
                      <ParticipantCard participant={external.contacts.dayOf} type="external" />
                    )}
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Internal Staff */}
        <Grid item xs={12} md={hasExternal ? 6 : 12}>
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: "50%",
                    bgcolor: "primary.main",
                  }}
                />
                <Typography variant="subtitle1" fontWeight={700}>
                  Internal Staff
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />

              {internal.length === 0 ? (
                <Alert severity="info" sx={{ mb: 2 }}>
                  No internal staff assigned to this order
                </Alert>
              ) : (
                <Box>
                  {internal.map((staff, idx) => (
                    <ParticipantCard key={staff.id || idx} participant={staff} type="internal" />
                  ))}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
